import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, Lock, User, AlertCircle, CheckCircle, CreditCard, Loader2 } from 'lucide-react';
import {
  fetchPricingModulesWithFallback,
  fetchUserTiersWithFallback,
  postPricingCalculate,
  createSubscription,
  getPublicCheckoutUrl,
} from '../../services/subscriptionService';
import { apiPath } from '../../utils/apiBaseUrl';
import { isTenantSubdomain } from '../../utils/tenantProfile';
import { userFacingError } from '../../i18n/userFacingError';
import i18n from '../../i18n';

const TENANT_DOMAIN = process.env.REACT_APP_TENANT_BASE_DOMAIN || 'microservices.local';

const tenantPortalUrl = (slug) => {
  const scheme = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'https' : 'http';
  return `${scheme}://${slug}.${TENANT_DOMAIN}`;
};

const CYCLES = [
  { id: 'monthly', label: 'Mensal' },
  { id: 'semiannual', label: 'Semestral' },
  { id: 'annual', label: 'Anual' },
];

/** Trial fixo no cadastro; não exibido no formulário. */
const DEFAULT_TRIAL_DAYS = 7;

const onlyDigits = (v = '') => v.replace(/\D+/g, '');
const maskCNPJ = (v) => {
  const s = onlyDigits(v).slice(0, 14);
  if (s.length <= 2) return s;
  if (s.length <= 5) return `${s.slice(0, 2)}.${s.slice(2)}`;
  if (s.length <= 8) return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5)}`;
  if (s.length <= 12) return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5, 8)}/${s.slice(8)}`;
  return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5, 8)}/${s.slice(8, 12)}-${s.slice(12, 14)}`;
};

const fmtBrl = (cents) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const RegisterOrganization = () => {
  const navigate = useNavigate();
  const onTenantPortal = isTenantSubdomain(
    typeof window !== 'undefined' ? window.location.hostname : '',
    process.env.REACT_APP_TENANT_BASE_DOMAIN || TENANT_DOMAIN,
  );

  useEffect(() => {
    if (onTenantPortal) navigate('/login', { replace: true });
  }, [navigate, onTenantPortal]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [pricing, setPricing] = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [pricingFromFallback, setPricingFromFallback] = useState(false);
  const [selected, setSelected] = useState(() => new Set(['base']));
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [userTiers, setUserTiers] = useState([]);
  const [userTierCode, setUserTierCode] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [previewCents, setPreviewCents] = useState(null);
  const [form, setForm] = useState({
    slug: '',
    name: '',
    org_type: 'MUNICIPALITY',
    tenant_profile: 'orgao',
    admin_username: '',
    admin_email: '',
    admin_password: '',
    admin_first_name: '',
    admin_last_name: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPricing(true);
      setPricingFromFallback(false);
      try {
        const { list, fromFallback } = await fetchPricingModulesWithFallback();
        const { list: tierList } = await fetchUserTiersWithFallback();
        if (!cancelled) {
          setPricing(list);
          setPricingFromFallback(fromFallback);
          setUserTiers(tierList);
          setUserTierCode((prev) => prev || tierList[0]?.code || '');
        }
      } finally {
        if (!cancelled) setLoadingPricing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const codes = Array.from(selected);
    if (codes.length === 0) {
      setPreviewCents(null);
      return;
    }
    postPricingCalculate({ module_codes: codes, billing_cycle: billingCycle, user_tier_code: userTierCode || null })
      .then((r) => {
        const n = r.data?.price_period_cents;
        setPreviewCents(typeof n === 'number' ? n : null);
      })
      .catch(() => setPreviewCents(null));
  }, [selected, billingCycle, userTierCode]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => {
      if (name === 'org_type') {
        return {
          ...p,
          org_type: value,
          tenant_profile: value === 'COMPANY' ? 'consultoria' : 'orgao',
        };
      }
      return { ...p, [name]: value };
    });
    setError('');
  };

  const toggleModule = (code) => {
    const c = String(code).toLowerCase();
    if (c === 'base') return;
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(c)) n.delete(c);
      else n.add(c);
      if (!n.has('base')) n.add('base');
      return n;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(null);
    try {
      const { data: orgData } = await axios.post(apiPath('/api/v1/organizations/register-with-admin'), {
        slug: form.slug.trim().toLowerCase(),
        name: form.name.trim(),
        org_type: form.org_type,
        tenant_profile: form.tenant_profile,
        admin: {
          username: form.admin_username.trim(),
          email: form.admin_email.trim(),
          password: form.admin_password,
          first_name: form.admin_first_name.trim(),
          last_name: form.admin_last_name.trim(),
        },
      });
      const orgId = orgData.organization_id;
      if (!orgId) {
        throw new Error('Resposta do cadastro sem organization_id');
      }

      const cnpjDigits = onlyDigits(cnpj);
      let paymentUrl = null;
      let checkoutMessage = null;
      let subWarnings = [];
      let subscriptionErr = null;

      try {
        const subRes = await createSubscription({
          organization_id: orgId,
          module_codes: Array.from(selected),
          billing_cycle: billingCycle,
          user_tier_code: userTierCode || null,
          trial_days: DEFAULT_TRIAL_DAYS,
          organization_name: form.name.trim(),
          organization_email: form.admin_email.trim(),
          organization_cnpj: cnpjDigits.length === 14 ? cnpjDigits : null,
        });
        const sub = subRes.data;
        subWarnings = sub.gateway_warnings || [];
        const token = sub.checkout_token;
        if (token) {
          try {
            const co = await getPublicCheckoutUrl(orgId, token);
            paymentUrl = co.data?.url || null;
            checkoutMessage = co.data?.message || null;
          } catch (ce) {
            checkoutMessage = i18n.t('checkoutError', { ns: 'billing' });
          }
        }
      } catch (se) {
        subscriptionErr = userFacingError(se, 'billing:checkoutError');
      }

      setSuccess({
        ...orgData,
        paymentUrl,
        checkoutMessage,
        subWarnings,
        subscriptionErr,
      });
    } catch (err) {
      setError(userFacingError(err, 'registerOrgError'));
    } finally {
      setLoading(false);
    }
  };

  if (onTenantPortal) return null;

  if (success) {
    const url = tenantPortalUrl(success.slug);
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-12">
        <div className="w-full max-w-lg overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="h-1.5 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-900" aria-hidden />
          <div className="p-8 text-center">
            <CheckCircle className="h-14 w-14 text-green-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Organização criada</h2>
            <p className="text-gray-600 mb-4">{success.message}</p>
            <p className="text-sm text-gray-700 mb-4">
              Acesse o portal em:{' '}
              <strong className="text-primary-700">{url}</strong>
              <span className="block text-xs text-gray-500 mt-1">(configure DNS ou /etc/hosts apontando para o cluster)</span>
            </p>
            {success.subscriptionErr && (
              <p className="text-sm text-amber-800 bg-amber-50 rounded-lg p-3 mb-4 text-left">
                {success.subscriptionErr}
              </p>
            )}
            {success.subWarnings?.length > 0 && (
              <p className="text-sm text-amber-800 bg-amber-50 rounded-lg p-3 mb-4 text-left">
                {success.subWarnings.join(' ')}
              </p>
            )}
            {success.paymentUrl && (
              <a
                href={success.paymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary w-full py-3 rounded-lg mb-3 inline-block"
              >
                Abrir pagamento (Asaas)
              </a>
            )}
            {success.checkoutMessage && !success.paymentUrl && (
              <p className="text-xs text-gray-500 mb-4">{success.checkoutMessage}</p>
            )}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="btn-primary w-full py-3 rounded-lg"
            >
              Ir para login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-12 sm:px-6">
      <div className="w-full max-w-2xl">
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="h-1.5 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-900" aria-hidden />
          <div className="p-8">
            <div className="text-center mb-6">
              <Building2 className="h-12 w-12 text-primary-600 mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-gray-900">Cadastrar organização</h2>
              <p className="text-sm text-gray-600 mt-1">
                Primeiro passo no modelo SaaS: cria seu <strong className="font-medium text-gray-800">tenant</strong>{' '}
                (órgão ou empresa) e o <strong className="font-medium text-gray-800">usuário administrador</strong>.
                Estados, prefeituras ou empresa — escolha de módulos e plano antes de concluir.
              </p>
              <p className="text-xs text-gray-500 mt-2 max-w-lg mx-auto">
                O cadastro de pessoas físicas, PJ e demais contas com acesso ao sistema deve ser feito depois do login pelos usuários autorizados.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm flex gap-2 items-start">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={onSubmit} className="form-stack">
              <div className="form-group">
                <label className="form-label">Subdomínio (slug)</label>
                <input
                  name="slug"
                  value={form.slug}
                  onChange={onChange}
                  required
                  pattern="[a-z0-9][a-z0-9-]*[a-z0-9]|[a-z0-9]{2}"
                  className="form-input"
                  placeholder="ex.: maceio"
                />
                <p className="form-help">URL: {form.slug || 'slug'}.{TENANT_DOMAIN}</p>
              </div>
              <div className="form-group">
                <label className="form-label">Nome da organização</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  className="form-input"
                  placeholder="Prefeitura de Maceió"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Tipo</label>
                <select
                  name="org_type"
                  value={form.org_type}
                  onChange={onChange}
                  className="form-select"
                >
                  <option value="MUNICIPALITY">Prefeitura / Município</option>
                  <option value="STATE">Estado</option>
                  <option value="COMPANY">Consultoria / empresa</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Modo de uso</label>
                <select
                  name="tenant_profile"
                  value={form.tenant_profile}
                  onChange={onChange}
                  className="form-select"
                >
                  <option value="orgao">Órgão ambiental</option>
                  <option value="consultoria">Consultoria</option>
                </select>
                <p className="form-help">
                  Pré-selecionado pelo tipo, mas pode ser alterado (ex.: prefeitura em modo consultoria).
                </p>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <p className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <User className="h-4 w-4" /> Administrador
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    name="admin_first_name"
                    value={form.admin_first_name}
                    onChange={onChange}
                    required
                    placeholder="Nome"
                    className="form-input"
                  />
                  <input
                    name="admin_last_name"
                    value={form.admin_last_name}
                    onChange={onChange}
                    required
                    placeholder="Sobrenome"
                    className="form-input"
                  />
                </div>
                <input
                  name="admin_username"
                  value={form.admin_username}
                  onChange={onChange}
                  required
                  placeholder="Username"
                  className="form-input mt-3"
                />
                <input
                  name="admin_email"
                  type="email"
                  value={form.admin_email}
                  onChange={onChange}
                  required
                  placeholder="E-mail (também usado no faturamento)"
                  className="form-input mt-3"
                />
                <div className="relative mt-3">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    name="admin_password"
                    type="password"
                    value={form.admin_password}
                    onChange={onChange}
                    required
                    minLength={8}
                    placeholder="Senha (mín. 8 caracteres)"
                    className="form-input pl-10"
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6 mt-6">
                <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <CreditCard className="h-4 w-4" />
                  Plano: módulos e pagamento
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  O módulo <strong>base</strong> fica sempre ativo. Com Asaas e API ativa, o CNPJ (14 dígitos) é
                  obrigatório para gerar a cobrança. A assinatura inclui <strong>7 dias de teste</strong> antes do
                  início da cobrança, conforme regra do Asaas.
                </p>
                {pricingFromFallback && (
                  <p className="text-xs text-amber-800 bg-amber-50 rounded-lg p-3 mb-4">
                    Não foi possível carregar a tabela de preços do servidor; exibindo módulos padrão. Quando o
                    subscription-service estiver no ar, os valores oficiais serão aplicados.
                  </p>
                )}
                {loadingPricing ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Carregando preços…
                  </div>
                ) : (
                  <>
                    <div className="form-group max-w-sm mb-4">
                      <label className="form-label" htmlFor="reg-billing-cycle">
                        Periodicidade
                      </label>
                      <select
                        id="reg-billing-cycle"
                        className="form-select"
                        value={billingCycle}
                        onChange={(e) => setBillingCycle(e.target.value)}
                      >
                        {CYCLES.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group max-w-sm mb-4">
                      <label className="form-label" htmlFor="reg-user-tier">
                        Quantidade de usuários
                      </label>
                      <select
                        id="reg-user-tier"
                        className="form-select"
                        value={userTierCode}
                        onChange={(e) => setUserTierCode(e.target.value)}
                      >
                        {userTiers.map((tier) => (
                          <option key={tier.code} value={tier.code}>
                            {tier.display_name}
                            {tier.price_monthly_cents ? ` · +${fmtBrl(tier.price_monthly_cents)}/mês` : ' · incluído'}
                          </option>
                        ))}
                      </select>
                      <p className="mt-2 text-xs text-gray-500">
                        Vale para usuários internos (servidores e equipe). Requerentes que acessam o portal não ocupam
                        vaga e não têm limite.
                      </p>
                    </div>
                    <div className="form-group mb-4">
                      <label className="form-label">CNPJ da entidade (faturamento)</label>
                      <input
                        type="text"
                        className="form-input max-w-md"
                        value={cnpj}
                        onChange={(e) => setCnpj(maskCNPJ(e.target.value))}
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {pricing
                        .filter((p) => p.is_active !== false)
                        .map((p) => {
                          const code = String(p.code).toLowerCase();
                          return (
                            <label
                              key={p.code}
                              className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2"
                            >
                              <input
                                type="checkbox"
                                className="form-check mt-1"
                                checked={selected.has(code)}
                                disabled={code === 'base'}
                                onChange={() => toggleModule(p.code)}
                              />
                              <div>
                                <div className="text-sm font-medium text-gray-900">{p.display_name || p.code}</div>
                                <div className="text-xs text-gray-500">
                                  {p.code} · {fmtBrl(p.price_monthly_cents)}/mês
                                </div>
                              </div>
                            </label>
                          );
                        })}
                    </div>
                    {previewCents != null && (
                      <p className="mt-3 text-sm text-gray-700">
                        Próxima fatura (referência): <strong>{fmtBrl(previewCents)}</strong>
                      </p>
                    )}
                  </>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || loadingPricing}
                className="btn btn-primary w-full rounded-lg py-3 font-medium disabled:opacity-50"
              >
                {loading ? 'Criando…' : 'Criar organização, administrador e plano'}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-600">
              <Link to="/login" className="text-primary-600 hover:underline">
                Voltar ao login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterOrganization;
